import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const ORANGE = '#F18A00';
const BASE = '/api';
const TOKEN = () => localStorage.getItem('arcus_token');
function authFetch(url, opts = {}) {
  return fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      ...(opts.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(TOKEN() ? { Authorization: `Bearer ${TOKEN()}` } : {}),
    },
  });
}

const Icon = ({ color = 'currentColor', size = 22, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
// Icône "notes internes" : bloc-notes / archive (distinct de la bulle de chat externe).
const IconNotes = (c, s) => <Icon color={c} size={s}><path d="M9 5h6a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2"/><path d="M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2 -2"/><path d="M9 12h6"/><path d="M9 16h4"/></Icon>;
const IconSend  = (c) => <Icon color={c} size={18}><path d="M10 14l11 -11"/><path d="M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -8l-8 -3.5a.55 .55 0 0 1 0 -1l18 -6.5"/></Icon>;
const IconPaperclip = (c) => <Icon color={c} size={16}><path d="M15 7l-6.5 6.5a2.12 2.12 0 0 0 3 3l6.5 -6.5a4.24 4.24 0 0 0 -6 -6l-6.5 6.5a6.36 6.36 0 0 0 9 9l6.5 -6.5"/></Icon>;
const IconClose = (c) => <Icon color={c} size={20}><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></Icon>;
const IconTrash = (c) => <Icon color={c} size={15}><path d="M4 7l16 0"/><path d="M10 11l0 6"/><path d="M14 11l0 6"/><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"/><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/></Icon>;
const IconFile  = (c) => <Icon color={c} size={16}><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/></Icon>;

function fmtDateTime(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' · ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
function fmtSize(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function NotesInternesPanel() {
  const location = useLocation();
  const { user, isArcus, isIT } = useAuth();
  const { t } = useLanguage();

  const [open, setOpen]       = useState(false);
  const [target, setTarget]   = useState(null); // { kind:'client'|'police', id }
  const [notes, setNotes]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText]       = useState('');
  const [files, setFiles]     = useState([]);   // File[]
  const [sending, setSending] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError]     = useState('');

  const fileInputRef = useRef(null);
  const scrollRef    = useRef(null);

  // Écriture : manager, souscripteur, it (pas accounts).
  const canWrite = ['manager', 'souscripteur', 'it'].includes(user?.role);

  // ── Détecter la cible depuis l'URL ─────────────────────────────
  // /clients/:id/detail  → client ; /polices/:id ou /gf/:id → police.
  useEffect(() => {
    const p = location.pathname;
    let tg = null;
    let m;
    if ((m = p.match(/^\/clients\/([0-9a-f-]{36})\/detail/i))) tg = { kind: 'client', id: m[1] };
    else if ((m = p.match(/^\/polices\/([0-9a-f-]{36})/i)))    tg = { kind: 'police', id: m[1] };
    else if ((m = p.match(/^\/gf\/([0-9a-f-]{36})/i)))         tg = { kind: 'police', id: m[1] };
    setTarget(tg);
    if (!tg) setOpen(false);
  }, [location.pathname]);

  const load = useCallback(async () => {
    if (!target) return;
    setLoading(true); setError('');
    try {
      const url = target.kind === 'client'
        ? `${BASE}/notes-internes/by-client/${target.id}`
        : `${BASE}/notes-internes/by-police/${target.id}`;
      const r = await authFetch(url);
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Erreur'); setNotes([]); return; }
      setNotes(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [target]);

  useEffect(() => { if (open && target) load(); }, [open, target, load]);

  // Défiler vers le bas à l'arrivée de nouveaux messages.
  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [notes, open]);

  function addFiles(list) {
    const arr = Array.from(list || []);
    if (arr.length) setFiles(prev => [...prev, ...arr]);
  }
  function removeFile(i) { setFiles(prev => prev.filter((_, idx) => idx !== i)); }

  async function send() {
    if (!target || (!text.trim() && files.length === 0)) return;
    setSending(true); setError('');
    try {
      const fd = new FormData();
      fd.append('body', text);
      files.forEach(f => fd.append('fichiers', f));
      const url = target.kind === 'client'
        ? `${BASE}/notes-internes/by-client/${target.id}`
        : `${BASE}/notes-internes/by-police/${target.id}`;
      const r = await authFetch(url, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Erreur'); return; }
      setNotes(Array.isArray(data) ? data : []);
      setText(''); setFiles([]);
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  }

  async function del(id) {
    if (!isIT) return;
    if (!window.confirm(t('notes_int.delete_confirm') === 'notes_int.delete_confirm' ? 'Supprimer ce commentaire ?' : t('notes_int.delete_confirm'))) return;
    try {
      const r = await authFetch(`${BASE}/notes-internes/${id}`, { method: 'DELETE' });
      if (r.ok) setNotes(prev => prev.filter(n => n.id !== id));
    } catch { /* silencieux */ }
  }

  function openFile(fid) {
    // Ouvre la pièce jointe dans un nouvel onglet (avec le token via fetch → blob).
    authFetch(`${BASE}/notes-internes/fichiers/${fid}`)
      .then(r => r.blob())
      .then(b => { const u = URL.createObjectURL(b); window.open(u, '_blank'); })
      .catch(() => {});
  }

  // Réservé aux profils Arcus, et seulement sur une fiche client/contrat.
  if (!isArcus || !target) return null;

  const tr = (k, fb) => (t(k) === k ? fb : t(k));

  // ── Bulle minimisée (au-dessus du chat externe) ────────────────
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{
          position: 'fixed', right: 22, bottom: 88, width: 56, height: 56,
          borderRadius: '50%', background: '#161616', color: '#fff',
          border: `2px solid ${ORANGE}`, cursor: 'pointer', zIndex: 9998,
          boxShadow: '0 6px 18px rgba(0,0,0,.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        title={tr('notes_int.open', 'Discussion interne')}>
        {IconNotes(ORANGE, 24)}
      </button>
    );
  }

  // ── Panneau complet ────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 380, maxWidth: '100vw',
      background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,.14)', zIndex: 9998,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* En-tête */}
      <div style={{ background: '#161616', color: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 11 }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(241,138,0,.16)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {IconNotes(ORANGE, 20)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{tr('notes_int.title', 'Discussion interne')}</div>
          <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>
            {target.kind === 'client' ? tr('notes_int.scope_client', 'Fil rattaché à ce client') : tr('notes_int.scope_police', 'Fil rattaché à ce contrat')}
          </div>
        </div>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          {IconClose('#fff')}
        </button>
      </div>

      {/* Liste des commentaires */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#FAFAF9' }}>
        {loading ? (
          <div style={{ color: '#64748B', fontSize: 13, textAlign: 'center', padding: 20 }}>{tr('common.loading', 'Chargement…')}</div>
        ) : notes.length === 0 ? (
          <div style={{ color: '#94A3B8', fontSize: 13, textAlign: 'center', padding: '32px 12px', lineHeight: 1.6 }}>
            {tr('notes_int.empty', 'Aucun échange interne pour le moment. Ajoutez un commentaire pour démarrer l\'historique de ce dossier.')}
          </div>
        ) : notes.map(n => (
          <div key={n.id} style={{ background: '#fff', border: '1px solid #EAEAE7', borderRadius: 10, padding: '11px 13px', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#FEF3E2', color: '#9A5A00', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {(n.auteur || '?').slice(0, 1).toUpperCase()}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1A1A1A' }}>{n.auteur}</div>
                <div style={{ fontSize: 11, color: '#94A3B8' }}>{fmtDateTime(n.created_at)}</div>
              </div>
              {isIT && (
                <button onClick={() => del(n.id)} title={tr('common.delete', 'Supprimer')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B91C1C', padding: 3, opacity: .7 }}>
                  {IconTrash('#B91C1C')}
                </button>
              )}
            </div>
            {n.body && <div style={{ fontSize: 13.5, color: '#1E293B', whiteSpace: 'pre-wrap', lineHeight: 1.5, marginBottom: n.fichiers?.length ? 8 : 0 }}>{n.body}</div>}
            {(n.fichiers || []).map(f => (
              <button key={f.id} onClick={() => openFile(f.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                  background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '7px 10px', marginTop: 5, cursor: 'pointer' }}>
                <span style={{ color: ORANGE, display: 'inline-flex' }}>{IconFile(ORANGE)}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: '#1D4ED8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fichier_nom}</span>
                <span style={{ fontSize: 11, color: '#94A3B8' }}>{fmtSize(f.taille)}</span>
              </button>
            ))}
          </div>
        ))}
        {error && <div style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8 }}>⚠ {error}</div>}
      </div>

      {/* Zone de saisie — seulement pour les profils autorisés */}
      {canWrite ? (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          style={{ borderTop: '1px solid #EAEAE7', padding: 12, background: dragOver ? '#FFF7F2' : '#fff' }}>
          {dragOver && (
            <div style={{ border: `2px dashed ${ORANGE}`, borderRadius: 8, padding: '10px', textAlign: 'center', color: '#9A5A00', fontSize: 12.5, marginBottom: 8 }}>
              {tr('notes_int.drop_here', 'Déposez les fichiers ici')}
            </div>
          )}
          {/* Fichiers en attente d'envoi */}
          {files.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {files.map((f, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#F1F5F9', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>
                  {IconFile('#64748B')}
                  <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 0, display: 'inline-flex' }}>{IconClose('#94A3B8')}</button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <button onClick={() => fileInputRef.current?.click()} title={tr('notes_int.attach', 'Joindre des fichiers')}
              style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '9px', cursor: 'pointer', display: 'inline-flex', flexShrink: 0 }}>
              {IconPaperclip('#64748B')}
            </button>
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
            <textarea value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
              placeholder={tr('notes_int.placeholder', 'Ajouter un commentaire interne…')} rows={2}
              style={{ flex: 1, resize: 'none', border: '1px solid #E2E8F0', borderRadius: 8, padding: '9px 11px', fontSize: 13.5, fontFamily: 'inherit', outline: 'none' }} />
            <button onClick={send} disabled={sending || (!text.trim() && files.length === 0)}
              style={{ background: (sending || (!text.trim() && files.length === 0)) ? '#E2E2DF' : ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 11px', cursor: (sending || (!text.trim() && files.length === 0)) ? 'not-allowed' : 'pointer', display: 'inline-flex', flexShrink: 0 }}>
              {IconSend('#fff')}
            </button>
          </div>
          <div style={{ fontSize: 10.5, color: '#B8B8B3', marginTop: 5 }}>{tr('notes_int.hint', 'Glissez-déposez un fichier ou un email · Ctrl+Entrée pour envoyer')}</div>
        </div>
      ) : (
        <div style={{ borderTop: '1px solid #EAEAE7', padding: '12px 14px', fontSize: 12.5, color: '#94A3B8', background: '#fff', textAlign: 'center' }}>
          {tr('notes_int.readonly', 'Consultation seule pour ce profil.')}
        </div>
      )}
    </div>
  );
}
